// test start ENG-18988 prevCareCompare
// packages
import React from "react";

// components
import { Heading } from "@/shared/components/ui/Heading";
import { PlanToggleButton } from "./PlanToggleButton";

// utils
import { cn } from "@/shared/utils";
import Strings from "@/shared/utils/Strings.constants";
import { PolicyUtils } from "@/shared/utils/PolicyUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { PreventiveCoverageType, PreventiveOption, PreventiveConfig } from "@/shared/utils/CoverageUtils";
import { buildCompareRows, COMPARE_GROUPS, COMPARE_FLAT_ORDER, CompareGroupId, CompareRow, CompareRowId } from "./compareRows";

// contexts
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";

// media
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserNurse, faStethoscope, faSyringe } from "@fortawesome/pro-duotone-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import GoldPreventive from "@/shared/media/images/gold-preventive.svg";
import PlatinumPreventive from "@/shared/media/images/platinum-preventive.svg";
import GoldPreventiveRebrand from "@/shared/media/images/gold-preventive-rebrand.svg";
import PlatinumPreventiveRebrand from "@/shared/media/images/platinum-preventive-rebrand.svg";
import PointingArrow from "@/shared/media/images/pointing-arrow.svg";

// types
import { Pet, Quote } from "@/shared/types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";

export type PrevCareCompareVariant = "compareTableCategorized" | "compareTable";

/** `"control"`, `""` and any unrecognised console value fall through to the control experience. */
export const isPrevCareCompareVariant = (value?: string): value is PrevCareCompareVariant => value === "compareTableCategorized" || value === "compareTable";

interface PreventiveCompareTableProps {
    pet: Pet;
    preventiveOptions: PreventiveOption[];
    preventiveConfig: PreventiveConfig;
    underwriter: PetUnderwriterType;
    wellnessLevel: string;
    handleChange: (coverageLevel: PreventiveCoverageType) => void;
    variant: PrevCareCompareVariant;
}

const COPY = Strings.PREVENTIVE_COMPARE;

const GROUP_ICONS: Record<CompareGroupId, IconDefinition> = {
    treatments: faUserNurse,
    tests: faStethoscope,
    vaccines: faSyringe
};

/**
 * From the frames: the benefit column is 1.35fr below `sm` and 1.5fr from `sm` up, and the three
 * columns sit flush below `sm`, then separate by 16px (`sm`/`md`) and 24px (`lg`/`xl`).
 */
const GRID_COLUMNS = "grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-x-4 lg:gap-x-6";

/** Shared by every cell in a plan column so the column reads as one continuous card. */
const planColumnCell = "border-x border-stroke-primary bg-background-primary";

const AmountCell = ({ amount, note, highlight, testId }: { amount: number | null; note?: string; highlight?: boolean; testId: string }) => (
    <div role="cell" data-testid={testId} className={cn(planColumnCell, "flex items-stretch justify-center px-0.5 py-1 sm:px-2", { "p-2": highlight })}>
        {/* the highlight is a rounded block inside the cell, not the full-bleed cell */}
        <div className={cn("py-px flex flex-1 flex-col items-center justify-center rounded-md px-2 text-center", highlight && "bg-background-success")}>
            {amount === null ? (
                <span className="text-base font-medium leading-5 text-content-inactive">{COPY.NOT_INCLUDED}</span>
            ) : (
                <>
                    <span className="text-sm font-bold leading-5 text-content-primary">{UIUtils.formatCurrency(amount, 0)}</span>
                    {note && <span className="text-[10px] font-medium italic leading-[14px] text-content-warning">{note}</span>}
                </>
            )}
        </div>
    </div>
);

export function PreventiveCompareTable({ pet, preventiveOptions, preventiveConfig, underwriter, wellnessLevel, handleChange, variant }: PreventiveCompareTableProps) {
    const { showRebrandTheme } = useThemeContext().theme;
    const { watch } = useFormParentContext<Quote>();
    const quote = watch();
    const { activePetIndex, isApplyAllHidden, isMultiPetLinked, isQuoteUpdating } = useAppLayerContext().appState;

    const compareTableCategorized = variant === "compareTableCategorized";
    const goldLevel = preventiveOptions.find(option => option.value === "wellness-gold");
    const platinumLevel = preventiveOptions.find(option => option.value === "wellness-platinum");

    // D9 — under apply-all the personalization describes the FIRST pet added, not the pet being
    // edited. Everything age-derived must come from this one pet: the badge AND the rows, since the
    // dental row's label flips to "Spay/Neuter or…" under 3 (IconUtils). Reading the badge from the
    // first pet while the rows read the active pet makes the badge describe a different animal than
    // the row it points at.
    const personalizationPet = isMultiPetLinked && !isApplyAllHidden ? ((quote?.policies?.[0] as Pet | undefined) ?? pet) : pet;
    const showPopularBadge = Number.isInteger(personalizationPet?.age) && !!personalizationPet?.species;
    const popularForText = showPopularBadge ? PolicyUtils.recommendedPreventiveText(personalizationPet.species, personalizationPet.age as number) : "";

    const rows = buildCompareRows(underwriter, {
        petAge: personalizationPet.age as number,
        quote,
        activePetIndex,
        isApplyAllHidden,
        applyToAllPets: isMultiPetLinked
    });

    const orderedIds: CompareRowId[] = variant === "compareTable" ? COMPARE_FLAT_ORDER : COMPARE_GROUPS.flatMap(group => group.rowIds);
    const totalGoldBenefits = orderedIds.reduce((total, id) => total + (rows.get(id)?.gold ?? 0), 0);

    // badge sizes step per breakpoint: 27x32 / 34x40 / 41x48 (platinum) and 32 / 40 / 48 (gold)
    const platinumImageClass = "h-8 w-[27px] shrink-0 sm:h-10 sm:w-[34px] lg:h-12 lg:w-[41px]";
    const goldImageClass = "size-8 shrink-0 sm:size-10 lg:size-12";
    const platinumImage = showRebrandTheme ? <PlatinumPreventiveRebrand className={platinumImageClass} /> : <PlatinumPreventive className={platinumImageClass} />;
    const goldImage = showRebrandTheme ? <GoldPreventiveRebrand className={goldImageClass} /> : <GoldPreventive className={goldImageClass} />;

    const isPlatinumActive = wellnessLevel === "wellness-platinum";
    const isGoldActive = wellnessLevel === "wellness-gold";
    const toggle = (level: PreventiveCoverageType, isActive: boolean) => handleChange(isActive ? "None" : level);

    const planHeaderCell = (options: { tier: "platinum" | "gold"; label: string; price?: number; subcopy: React.ReactNode; image: React.ReactNode }) => (
        <div
            role="columnheader"
            data-testid={`preventive-compare-header-${options.tier}`}
            className={cn(
                "relative flex flex-col items-center gap-2 rounded-t-xl border-x border-t border-stroke-primary px-2 pb-3 pt-5 text-center lg:justify-center lg:gap-1 lg:px-4 lg:py-3",
                options.tier === "platinum" ? "bg-background-prevcare" : "bg-background-warning"
            )}
        >
            {/*
             * "title + price": below lg the badge is lifted out of flow so it breaks the cell's top
             * edge and the name/price stack centres under it. From lg the badge rejoins the flow as
             * a row with the stack, which left-aligns beside it (the cell itself stays centred).
             */}
            <div className="flex w-full flex-col items-center gap-2 lg:w-auto lg:flex-row lg:justify-center lg:gap-4">
                <div className="absolute left-1/2 top-[-17px] -translate-x-1/2 sm:top-[-20px] lg:static lg:translate-x-0">{options.image}</div>
                <div className="flex flex-col items-center gap-1 lg:items-start">
                    <Heading
                        level="h3"
                        className={cn(
                            "text-base font-bold leading-5 sm:text-xl sm:leading-7 sm:tracking-[-0.1px]",
                            // The frames use a raw #2086b0 here, but that is 3.77:1 on the platinum header
                            // and the name is 16px bold below `sm` — normal-size text, so it needs 4.5:1.
                            // content-brand-b is 4.57:1, theme-aware, and already the group-title accent.
                            options.tier === "platinum" ? "text-content-brand-b" : "text-content-warning"
                        )}
                    >
                        {options.label}
                    </Heading>
                    {typeof options.price === "number" && (
                        <div data-testid={`preventive-compare-price-${options.tier}`} className="flex items-baseline text-content-primary">
                            <span className="text-base font-bold leading-5 sm:text-lg sm:leading-7">{UIUtils.formatCurrency(options.price)}</span>
                            <span className="text-[10px] font-medium leading-[14px] text-content-secondary sm:text-sm sm:leading-5">/mo</span>
                        </div>
                    )}
                </div>
            </div>
            <p className="text-xs font-medium leading-4 text-content-secondary">{options.subcopy}</p>
        </div>
    );

    const benefitRow = (row: CompareRow, isGrouped: boolean) => {
        // D9 — the dental row's platinum cell carries the age-personalization highlight
        const highlightPlatinum = showPopularBadge && row.id === "dental";

        return (
            <div key={row.id} role="row" className="contents">
                <div
                    role="rowheader"
                    data-testid={`preventive-compare-label-${row.id}`}
                    className={cn("flex items-center gap-1 py-1 text-xs font-medium leading-4 text-content-secondary sm:text-sm sm:leading-5", {
                        "sm:pl-5": isGrouped || !compareTableCategorized
                    })}
                >
                    <span aria-hidden="true" className="hidden sm:inline">
                        &bull;
                    </span>
                    <span>{row.label}</span>
                </div>
                <AmountCell amount={row.platinum} highlight={highlightPlatinum} testId={`preventive-compare-platinum-${row.id}`} />
                <AmountCell amount={row.gold} note={row.goldNote} testId={`preventive-compare-gold-${row.id}`} />
            </div>
        );
    };

    // ARIA table semantics are layered over the CSS grid rather than using <table>, which would
    // fight the per-cell column-card styling. Row wrappers are `display: contents` so the grid
    // still sees the cells as direct children; assistive tech gets row/column associations.
    return (
        <div role="table" aria-label={COPY.TABLE_LABEL} data-testid="preventive-compare-table" data-variant={variant} className={cn("grid", GRID_COLUMNS)}>
            {/* ---- header band ---- */}
            <div role="row" className="contents">
                <div role="columnheader" aria-label={COPY.BENEFITS_LABEL} className="flex flex-col gap-2 pb-2 pr-2 max-lg:justify-between lg:gap-6">
                    {showPopularBadge && (
                        <div data-testid="preventive-compare-popular-badge" className="flex w-full items-center pt-3">
                            <div className="relative w-full rounded-md bg-background-success p-2 pr-6 text-xs font-normal leading-4 text-content-success sm:pr-8 sm:text-sm lg:text-base">
                                {COPY.POPULAR_FOR} <span className="text-nowrap font-semibold">{popularForText}</span>
                                <PointingArrow
                                    aria-hidden="true"
                                    className="absolute right-[-16px] top-1/2 z-20 h-5 w-[40px] shrink-0 -translate-y-1/2 sm:right-[-46px] sm:h-7 sm:w-[70px]"
                                />
                            </div>
                        </div>
                    )}
                    <p className="text-xs font-bold leading-4 text-content-primary sm:text-sm">
                        <span data-testid="preventive-compare-benefits-label-mobile" className="sm:hidden">
                            {COPY.BENEFITS_LABEL_SHORT}
                        </span>
                        <span data-testid="preventive-compare-benefits-label-desktop" className="hidden sm:inline">
                            {COPY.BENEFITS_LABEL}
                        </span>
                    </p>
                </div>
                {planHeaderCell({
                    tier: "platinum",
                    label: preventiveConfig.labels.advanced,
                    price: platinumLevel?.price,
                    image: platinumImage,
                    subcopy: (
                        <>
                            <span className="sm:hidden">{COPY.PLATINUM_SUBCOPY_SHORTEST}</span>
                            <span className="hidden sm:inline lg:hidden">{COPY.PLATINUM_SUBCOPY_SHORT}</span>
                            <span className="hidden lg:inline">{COPY.PLATINUM_SUBCOPY}</span>
                        </>
                    )
                })}
                {planHeaderCell({
                    tier: "gold",
                    label: preventiveConfig.labels.basic,
                    price: goldLevel?.price,
                    image: goldImage,
                    subcopy: Strings.PREVENTIVE_COMPARE_GOLD_SUBCOPY(totalGoldBenefits)
                })}
            </div>

            {/* ---- body ---- */}
            {compareTableCategorized
                ? COMPARE_GROUPS.map(group => {
                      const groupRows = group.rowIds.map(id => rows.get(id)).filter((row): row is CompareRow => !!row);
                      if (!groupRows.length) return null;

                      return (
                          <React.Fragment key={group.id}>
                              <div role="row" className="contents">
                                  <div role="rowheader" aria-colspan={3} data-testid={`preventive-compare-group-${group.id}`} className="flex items-center gap-2 py-1 pr-2">
                                      <FontAwesomeIcon icon={GROUP_ICONS[group.id]} aria-hidden="true" className="size-5 text-content-brand-b" />
                                      <Heading level="h3" className="text-sm font-bold leading-5 text-content-brand-b sm:text-base">
                                          <span className="sm:hidden">{group.shortTitle}</span>
                                          <span className="hidden sm:inline">{group.title}</span>
                                      </Heading>
                                  </div>
                                  {/* empty plan cells keep the two column cards unbroken through the group header */}
                                  <div aria-hidden="true" className={cn(planColumnCell, "min-h-[28px] sm:min-h-[32px]")} />
                                  <div aria-hidden="true" className={cn(planColumnCell, "min-h-[28px] sm:min-h-[32px]")} />
                              </div>
                              {groupRows.map(row => benefitRow(row, true))}
                          </React.Fragment>
                      );
                  })
                : COMPARE_FLAT_ORDER.map(id => {
                      const row = rows.get(id);
                      return row ? benefitRow(row, false) : null;
                  })}

            {/* ---- footer band ---- */}
            <div role="row" className="contents">
                <div role="cell" />
                <div role="cell" className={cn("rounded-b-xl border-x border-b border-stroke-primary bg-background-secondary px-2 py-3 lg:px-4")}>
                    <PlanToggleButton
                        label={preventiveConfig.labels.advanced}
                        isActive={isPlatinumActive}
                        disabled={isQuoteUpdating}
                        onToggle={() => toggle("wellness-platinum", isPlatinumActive)}
                    />
                </div>
                <div role="cell" className={cn("rounded-b-xl border-x border-b border-stroke-primary bg-background-secondary px-2 py-3 lg:px-4")}>
                    <PlanToggleButton
                        label={preventiveConfig.labels.basic}
                        isActive={isGoldActive}
                        disabled={isQuoteUpdating}
                        onToggle={() => toggle("wellness-gold", isGoldActive)}
                    />
                </div>
            </div>
        </div>
    );
}

export default PreventiveCompareTable;
// test end
